import { Badge, type BadgeProps } from '@heidi/design-system/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { cn } from '@heidi/utils';
import {
  defineMessages,
  useIntl,
  type IntlShape,
  type MessageDescriptor,
} from 'react-intl';
import type { EnrichedReference } from '../../contracts';
import { formatSpecialty, isGuidelineSource, isPartnerCitation } from '../../contracts';
import { getAuthorityTier } from './authorityTiers';
import { getSourceTypeLabel } from './sourceIconUtils';

const messages = defineMessages({
  reliable: {
    defaultMessage: 'Reliable',
    id: 'QSGIiXAVQu',
    description: 'Badge label for a source with a high authoritativeness score',
  },
  unverified: {
    defaultMessage: 'Unverified',
    id: 'ukBVnoLuKx',
    description: 'Badge label for a source with a low authoritativeness score',
  },
  impactFactor: {
    defaultMessage: 'IF: {value}',
    id: '94pDFO+/OH',
    description: 'Journal impact factor shown in the source authority tooltip',
  },
});

const tierLabelMessages: Record<string, MessageDescriptor | undefined> = {
  'chat.reliable': messages.reliable,
  'chat.unverified': messages.unverified,
};

type BadgeSize = NonNullable<BadgeProps['size']>;

function buildTooltipContentFromReference(
  reference: EnrichedReference,
  intl: IntlShape
): string | undefined {
  const isPartnerSource = isPartnerCitation(reference.source_type);
  const showGuideline = !isPartnerSource && isGuidelineSource(reference.websiteType);

  const tooltipLines: string[] = [];

  if (!isPartnerSource && reference.websiteType?.[0]) {
    tooltipLines.push(getSourceTypeLabel(reference.websiteType[0]));
  }

  if (
    reference.content_type &&
    !(showGuideline && reference.content_type.toLowerCase().includes('guideline'))
  ) {
    tooltipLines.push(reference.content_type);
  }

  if (!isPartnerSource && reference.impact_factor !== undefined) {
    tooltipLines.push(
      intl.formatMessage(messages.impactFactor, {
        value: reference.impact_factor.toFixed(1),
      })
    );
  }

  if (!isPartnerSource && reference.specialty?.[0]) {
    tooltipLines.push(formatSpecialty(reference.specialty[0]));
  }

  return tooltipLines.length ? tooltipLines.join(' · ') : undefined;
}

interface AuthorityTierBadgeProps {
  score: number | null | undefined;
  size?: Extract<BadgeSize, 'sm' | 'md' | 'xs'>;
  className?: string;
  /** Optional: overrides computed tooltip content. */
  tooltipContent?: string;
  /**
   * Optional: when provided (and `tooltipContent` is not), tooltip content will be derived
   * from reference metadata (website type, content type, impact factor, specialty).
   */
  reference?: EnrichedReference;
}

export const AuthorityTierBadge = ({
  score,
  size = 'sm',
  className,
  tooltipContent,
  reference,
}: AuthorityTierBadgeProps) => {
  const intl = useIntl();
  const tier = getAuthorityTier(score);
  if (!tier) return null;

  const labelMessage = tierLabelMessages[tier.labelKey];
  if (!labelMessage) return null;

  const resolvedTooltipContent =
    tooltipContent ??
    (reference ? buildTooltipContentFromReference(reference, intl) : undefined);

  const badge = (
    <Badge
      color="neutral"
      size={size}
      data-testid="source-authority-tier"
      className={cn(tier.bgClass, tier.textClass, className)}
    >
      {intl.formatMessage(labelMessage)}
    </Badge>
  );

  if (!resolvedTooltipContent) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{badge}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {resolvedTooltipContent}
      </TooltipContent>
    </Tooltip>
  );
};
